/**
 * ACTIVITIES — the single content model for everything happening around the brand.
 *
 * ▸ Full how-to with copy-paste templates: see CONTENT-GUIDE.md in the repo root.
 *
 * To publish something new, add ONE entry to the `activities` array below and
 * assign it one or more categories. The site takes care of the rest:
 *
 *   - Every activity automatically appears in the "Latest" tab (newest first).
 *   - It also appears in each tab matching its categories.
 *   - Activities with an `eventDate` are grouped into Upcoming / Past on the
 *     Classes and Events tabs.
 *   - Activities with a `body` get a full story page at /activities/<id>
 *     and a "Read the Story" link on the timeline.
 *
 * Examples:
 *   - Clothing drop        → categories: ["merch"]
 *   - Podcast appearance   → categories: ["media"]
 *   - Pop-up class         → categories: ["classes", "events"]
 *   - Tour stop            → categories: ["events"]
 *   - News / article       → categories: [] (shows in Latest only)
 *
 * Field guide:
 *   publishedAt  ISO date ("2026-03-01") — controls ordering in "Latest".
 *   eventDate    ISO date — when the class/event happens (optional).
 *   displayDate  free-form override shown instead of the formatted date,
 *                e.g. "MAR 23–28, 2026" for multi-day events (optional).
 *   time         display string, e.g. "8:00 PM — 10:00 PM" (optional).
 *   location     display string, e.g. "Los Angeles — West Coast Dance" (optional).
 *   videoId      YouTube video id — renders an inline player (optional).
 *   image        imported image asset or URL — thumbnail on the timeline and
 *                hero image on the story page (optional).
 *   link         external call-to-action, e.g. tickets / shop / podcast (optional).
 *   reserve      true → shows the "Reserve Spot" payment-instructions flow.
 *   isFull       true → reserve button becomes a disabled "Class is Full".
 *   body         array of story blocks → full article page (optional, see below).
 *
 * Story blocks (the `body` array) — mix and order them freely:
 *   { type: "text",      text: "One paragraph per block." }
 *   { type: "heading",   text: "A Section Header" }
 *   { type: "quote",     text: "A big pull quote.", attribution: "Josh Smith" }
 *   { type: "image",     src: importedImage, alt: "...", caption: "..." }
 *   { type: "imagePair", left: { src: imgA, alt: "..." }, right: { src: imgB, alt: "..." }, caption: "..." }
 *   { type: "video",     videoId: "YouTubeId", caption: "..." }
 */

import a5 from  '../assets/a5.jpg';
import a6 from  '../assets/a6.jpg';
import a9 from  '../assets/a9.jpg';
import a10 from '../assets/a10.JPG';


export type ActivityCategory = "classes" | "events" | "media" | "merch";

export const CATEGORY_LABELS: Record<ActivityCategory, string> = {
  classes: "Classes",
  events: "Events",
  media: "Media",
  merch: "Merch",
};

export type ActivityBlock =
  | { type: "text"; text: string }
  | { type: "heading"; text: string }
  | { type: "quote"; text: string; attribution?: string }
  | { type: "image"; src: string; alt?: string; caption?: string }
  | {
      type: "imagePair";
      left: { src: string; alt?: string };
      right: { src: string; alt?: string };
      caption?: string;
    }
  | { type: "video"; videoId: string; caption?: string };

export interface Activity {
  id: string;
  title: string;
  description?: string;
  publishedAt: string;
  eventDate?: string;
  displayDate?: string;
  time?: string;
  location?: string;
  image?: string;
  videoId?: string;
  link?: { label: string; url: string };
  reserve?: boolean;
  isFull?: boolean;
  categories: ActivityCategory[];
  body?: ActivityBlock[];
}

export const activities: Activity[] = [
  /*{
    id: "from-durham-to-the-world-stage",
    title: "From Durham to the World Stage",
    description:
      "How a kid from North Carolina became one of the most in-demand movement artists of his generation.",
    publishedAt: "2026-07-01",
    image: a6,
    categories: [],
    body: [
      {
        type: "text",
        text: "His cultivation of highly successful productions as a Visionary, Creative Director and Choreographer, paired with his strong and youthful social media presence, makes Josh one of the most in-demand, and innovative movement artists of this generation.",
      },
      {
        type: "image",
        src: a5,
        alt: "Joshua Smith on stage with a microphone",
        caption: "On stage — where the work meets the audience.",
      },
      {
        type: "text",
        text: "Born in the small city of Durham, North Carolina, Josh spent his early industry years as a dancer in Atlanta, Georgia, touring with the likes of Usher and Austin Mahone. Despite great success, at the young age of 23, he quickly transitioned to behind the scenes, for some of the same artists he once toured for and more.",
      },
      {
        type: "quote",
        text: "Creative Direction allows me to dream and shape-shift reality. It is the gateway to giving the audience an experience they'll never forget.",
        attribution: "Josh Smith",
      },
      {
        type: "text",
        text: "From choreographing internationally sold out shows for over 100,000 screaming fans, to landing his own moves in virtual worlds like Fortnite, this Soul Train award-winning choreographer has catapulted his way into almost every artist's movement catalog. His work has appeared on stages like The Grammys, VMAs, SNL, Jimmy Fallon, Jimmy Kimmel, Billboard Awards, BET Awards and AMAs.",
      },
      {
        type: "imagePair",
        left: { src: a9, alt: "Joshua Smith backstage" },
        right: { src: a10, alt: "Joshua Smith with hands covering face" },
        caption: "Behind the scenes, between shows.",
      },
      {
        type: "video",
        videoId: "O6knMHWU4is",
        caption: "In rehearsal with Chris Brown.",
      },
      {
        type: "text",
        text: "During his off time from creating, Josh teaches around the world at conventions and international workshops, inspiring the masses and spreading the culture of today. Aside from his many accolades, his main passion will always be to bring purpose and storytelling to anything he touches. No dream is too big.",
      },
    ],
  },
  */
  
  {
    id: "shop-latest-collection",
    title: "Official Shop — New Collection Drop",
    description: "New pieces are live now in the official Dancer Boy Smith shop.",
    publishedAt: "2026-06-01",
    link: { label: "Shop Now", url: "https://shop.dancerboysmith.com" },
    categories: ["merch"],
  },
  {
    id: "popup-class-la-mar-2026",
    title: "Josh Smith Pop Up Class",
    description: "Advanced — Hip Hop.",
    publishedAt: "2026-02-14",
    eventDate: "2026-03-10",
    time: "8:00 PM — 10:00 PM",
    location: "Los Angeles — West Coast Dance",
    reserve: true,
    isFull: true,
    categories: ["classes", "events"],
  },
  {
    id: "btm-intensive-atl-2026",
    title: "Behind The Movement Live Intensive",
    description: "A week-long intensive in Atlanta.",
    publishedAt: "2026-02-01",
    eventDate: "2026-03-23",
    displayDate: "MAR 23–28, 2026",
    location: "Atlanta",
    link: { label: "Get Tickets", url: "https://www.jgreenproductions.com/btm-intensive" },
    categories: ["classes", "events"],
  },
  {
    id: "ourselves-podcast",
    title: "Ourselves Podcast — Guest Appearance",
    description: "Josh sits down to talk craft, career and creative direction.",
    publishedAt: "2026-01-20",
    videoId: "aiYDnNScamY",
    categories: ["media"],
  },
  {
    id: "bts-chris-brown",
    title: "Behind The Scenes — Chris Brown",
    description: "On set and in rehearsal.",
    publishedAt: "2025-12-05",
    videoId: "5pF4Hv3E1MU",
    categories: ["media"],
  },
];

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

/** "2026-03-10" → "MAR 10, 2026" (parsed manually to avoid timezone drift). */
export function formatActivityDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}

/** The date shown next to an activity: display override → event date → publish date. */
export function activityMetaDate(activity: Activity): string {
  return (
    activity.displayDate ||
    formatActivityDate(activity.eventDate || activity.publishedAt)
  );
}

/** Today as a local ISO date string, comparable against publishedAt/eventDate. */
export function todayISO(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}
